import classNames from 'classnames';
import { Flex } from 'components/Atoms/Flex/Flex';
import { Carousel, CarouselSlide } from 'components/Content/Atoms/Carousel';
import React from 'react';
import { ContentType } from 'types/publishedAssets';
import { Dialog } from 'components/Atoms/Dialog/Dialog';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { ContentContainer } from '../../Atoms/ContentContainer';
import styles from './ImageGallery.module.scss';
import { ImageContent } from '../Image/ImageContent';
import { ImageGalleryAtom } from './ImageGalleryAtom';

interface Props {
  content: ContentType;
}

export function ImageGalleryGrid(props: Props) {
  const { content } = props;
  const { getMediaItemFromComponent, getImageCropFromComponent, translate } = useGetContentTranslations();
  const [dialogIndex, setDialogIndex] = React.useState(-1);
  const [showDialog, setShowDialog] = React.useState(false);

  // special case if only 1 image (draw single image)
  if (content?.components?.length === 1) {
    return <ImageContent content={content} />;
  }
  const label = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');

  // special case if only 2 or 4 images (draw 2 column grid)
  const isTwoColumn = content?.components?.length === 2 || content?.components?.length === 4;

  return (
    <ContentContainer content={content}>
      <BlockHeading title={label} secondaryText={secondaryText} />
      <Flex className={styles.container} flexDirection="row" flexWrap="wrap" gap={16}>
        {content?.components?.map((component, imageIndex) => {
          const mediaItem = getMediaItemFromComponent(component);
          if (!mediaItem) return null;
          return (
            <input
              type="image"
              src={mediaItem.url}
              alt={mediaItem.alt_text}
              className={classNames(styles.image, isTwoColumn && styles.twoColumn)}
              onClick={() => {
                setDialogIndex(imageIndex);
                setShowDialog(true);
              }}
              key={`${mediaItem.url}-${imageIndex}`}
              id={`imageGrid-${content.id}-${imageIndex}`}
            />
          );
        })}
      </Flex>
      {showDialog && (
        <Dialog
          open={showDialog}
          setOpen={() => {
            setShowDialog(false);
            setTimeout(() => {
              // restore focus to previously selected image (timeout to wait until dialog is closed)
              const element = document.querySelector(`#imageGrid-${content.id}-${dialogIndex}`) as HTMLInputElement;
              if (element) {
                element.focus();
              }
            }, 50);
          }}
          classname={styles.dialogImage}
          close
          title={
            <div className={classNames(styles.sliderContainer, styles.sliderInDialog)}>
              <Carousel
                spaceBetween={50}
                slidesPerView={1}
                onSlideChange={(index) => {
                  setDialogIndex(index);
                }}
                initialSlide={dialogIndex}
              >
                {content?.components?.map((component, index) => {
                  const mediaItem = getMediaItemFromComponent(component);
                  if (!mediaItem) return null;
                  return (
                    <CarouselSlide key={`${mediaItem.url}-${index}`}>
                      <ImageGalleryAtom
                        url={mediaItem.url}
                        alt={mediaItem?.alt_text || ''}
                        caption={mediaItem.caption}
                        imageCrop={getImageCropFromComponent(component)}
                        className={styles.largeImage}
                      />
                    </CarouselSlide>
                  );
                })}
              </Carousel>
            </div>
          }
        />
      )}
    </ContentContainer>
  );
}
